import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { API_BASE_URL } from "../../config";
import useToast from "../../components/useToast";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppChip from "../../design-system/components/AppChip";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppToolbar from "../../design-system/components/AppToolbar";

// --------- helpers ---------
function norm(s) {
  return (s || "").toString().trim();
}
function isBlank(v) {
  return !norm(v);
}
function keyOf(category, page, subPage = "") {
  return `${norm(category)}::${norm(page)}::${norm(subPage)}`;
}

function buildTree(rows) {
  const byCategory = new Map();

  rows.forEach((r) => {
    const category = norm(r.category);
    const page = norm(r.page);
    const subPage = norm(r.subPage);

    if (isBlank(category) || isBlank(page)) return;

    if (!byCategory.has(category)) byCategory.set(category, new Map());
    const pageMap = byCategory.get(category);

    if (!pageMap.has(page)) pageMap.set(page, new Set());
    const subSet = pageMap.get(page);

    if (!isBlank(subPage)) subSet.add(subPage);
  });

  return Array.from(byCategory.entries()).map(([category, pagesMap]) => ({
    category,
    pages: Array.from(pagesMap.entries()).map(([page, subSet]) => ({
      page,
      subPages: Array.from(subSet.values()).sort((a, b) => a.localeCompare(b)),
    })),
  }));
}

function computeParentState({ category, page, subPages }, enabledMap) {
  const pageKey = keyOf(category, page, "");
  const pageOn = !!enabledMap[pageKey];

  if (!subPages || subPages.length === 0) {
    return { checked: pageOn, partial: false };
  }

  const childKeys = subPages.map((sp) => keyOf(category, page, sp));
  const allChildrenOn = childKeys.every((k) => !!enabledMap[k]);

  // partial only if main ON but not all children ON
  return { checked: pageOn, partial: pageOn && !allChildrenOn };
}

function defaultEnabledMap(rows) {
  // default FALSE (matches your seed)
  const m = {};
  rows.forEach((r) => {
    const category = norm(r.category);
    const page = norm(r.page);
    const subPage = norm(r.subPage);
    if (isBlank(category) || isBlank(page)) return;

    m[keyOf(category, page, subPage)] = false;
  });

  // Ensure main key exists for every page (even if it has children)
  const tree = buildTree(rows);
  tree.forEach((cat) => {
    cat.pages.forEach((p) => {
      const pageKey = keyOf(cat.category, p.page, "");
      if (m[pageKey] === undefined) m[pageKey] = false;
    });
  });

  return m;
}

function normalizeBackendConfig(config_json) {
  if (!config_json) return null;

  if (Array.isArray(config_json)) return config_json;

  if (typeof config_json === "string") {
    try {
      const parsed = JSON.parse(config_json);
      return normalizeBackendConfig(parsed);
    } catch {
      return null;
    }
  }

  if (typeof config_json === "object") {
    if (Array.isArray(config_json.subPages)) return config_json.subPages;
  }

  return null;
}

function mergeRowsFromBackend(prevRows, backendItems, fallbackCategory) {
  const out = [...prevRows];
  const seen = new Set(prevRows.map((r) => keyOf(r.category, r.page, r.subPage)));

  const getCategoryForPage = (page) =>
    prevRows.find((r) => r.page === page)?.category || fallbackCategory;

  backendItems.forEach((f) => {
    const page = (f?.feature_key || "").toString().trim();
    if (!page) return;

    const category = getCategoryForPage(page);

    // main row
    const mainKey = keyOf(category, page, "");
    if (!seen.has(mainKey)) {
      out.push({ category, page, subPage: "" });
      seen.add(mainKey);
    }

    // subpages from backend config_json
    const subs = normalizeBackendConfig(f.config_json);
    if (subs && Array.isArray(subs)) {
      subs.forEach((sp) => {
        const name = (sp?.name || "").toString().trim();
        if (!name) return;
        const k = keyOf(category, page, name);
        if (!seen.has(k)) {
          out.push({ category, page, subPage: name });
          seen.add(k);
        }
      });
    }
  });

  return out;
}

function applyBackendToEnabledMap(baseMap, currentRows, backendItems, fallbackCategory) {
  const next = { ...baseMap };

  const getCategoryForPage = (page) =>
    currentRows.find((r) => r.page === page)?.category || fallbackCategory;

  backendItems.forEach((f) => {
    const page = (f?.feature_key || "").toString().trim();
    if (!page) return;

    const category = getCategoryForPage(page);

    // main
    next[keyOf(category, page, "")] = !!f.enabled;

    // children
    const subs = normalizeBackendConfig(f.config_json);

    if (subs && Array.isArray(subs)) {
      subs.forEach((sp) => {
        const name = (sp?.name || "").toString().trim();
        if (!name) return;
        next[keyOf(category, page, name)] = !!sp.enabled;
      });
    } else {
      // if no backend children config: align UI children to main
      const uiSubpages = currentRows
        .filter((r) => r.page === page && r.subPage && r.subPage.trim() !== "")
        .map((r) => r.subPage);

      uiSubpages.forEach((sp) => {
        next[keyOf(category, page, sp)] = !!f.enabled;
      });
    }
  });

  return next;
}

// --------- component ---------
export default function FeatureSetupPanel({ tabKey, title, endpoint, defaultRows }) {
  const { t } = useTranslation();
  const { error } = useToast();

  // ✅ same token logic as your Availability page
  const token = localStorage.getItem("session_token");

  const fallbackCategory = useMemo(
    () => defaultRows?.[0]?.category || title,
    [defaultRows, title]
  );

  const STORAGE_KEY = useMemo(() => `timetab_feature_setup_${tabKey}_v1`, [tabKey]);
  const apiUrl = useMemo(() => `${API_BASE_URL}${endpoint}`, [endpoint]);

  const [rows, setRows] = useState(defaultRows);
  const tree = useMemo(() => buildTree(rows), [rows]);

  const [enabled, setEnabled] = useState(() => {
    const base = defaultEnabledMap(defaultRows);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return { ...base, ...parsed };
      }
    } catch {}
    return base;
  });

  // keep latest enabled in a ref for debounced saves
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const [query, setQuery] = useState("");
  const showOnlyEnabled = false;
  const [loadingState, setLoadingState] = useState(true);

  // per-feature saving indicator (optional but helpful)
  const [savingKeys, setSavingKeys] = useState({}); // { [feature_key]: true/false }

  // prevent autosave while initial fetch/hydration
  const hydratingRef = useRef(true);

  // per-feature debounce timers
  const timersRef = useRef({}); // { [feature_key]: timeoutId }

  // persist enabled locally
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
    } catch {}
  }, [enabled, STORAGE_KEY]);

  // ---------------- FETCH (once per tab) ----------------
  useEffect(() => {
    let alive = true;

    const fetchFeatures = async () => {
      setLoadingState(true);
      hydratingRef.current = true;

      try {
        const res = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const items = Array.isArray(res.data) ? res.data : [];
        if (!alive) return;

        const mergedRows = mergeRowsFromBackend(defaultRows, items, fallbackCategory);
        setRows(mergedRows);

        const base = defaultEnabledMap(mergedRows);
        const mergedEnabled = applyBackendToEnabledMap(base, mergedRows, items, fallbackCategory);
        setEnabled(mergedEnabled);

        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedEnabled));
        } catch {}
      } catch (e) {
        if (e?.response?.status === 401) {
          error?.(t("feature_setup.errors.unauthorized", { title }));
        } else {
          error?.(t("feature_setup.errors.load", { title }));
        }
      } finally {
        hydratingRef.current = false;
        if (alive) setLoadingState(false);
      }
    };

    fetchFeatures();
    return () => {
      alive = false;
      hydratingRef.current = false;
    };

    // stable deps -> no loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, tabKey]);

  // ---------------- AUTOSAVE CORE ----------------
  const buildSinglePayload = useCallback(
    (feature_key, enabledSnapshot) => {
      const cat = fallbackCategory;
      const mainEnabled = !!enabledSnapshot[keyOf(cat, feature_key, "")];

      // find subpages for this feature in current tree
      const pageNode =
        tree
          .flatMap((c) => c.pages.map((p) => ({ category: c.category, ...p })))
          .find((p) => p.page === feature_key) || null;

      const subPages = pageNode?.subPages || [];

      return {
        enabled: mainEnabled,
        description: null,
        config_json: subPages.length
          ? subPages.map((sp) => ({
              name: sp,
              enabled: !!enabledSnapshot[keyOf(cat, feature_key, sp)],
            }))
          : null,
      };
    },
    [tree, fallbackCategory]
  );

  const saveFeature = useCallback(
    async (feature_key) => {
      if (!token) {
        error?.(t("feature_setup.errors.no_token", { title }));
        return;
      }

      const payload = buildSinglePayload(feature_key, enabledRef.current);

      setSavingKeys((prev) => ({ ...prev, [feature_key]: true }));
      try {
        await axios.put(`${apiUrl}/${encodeURIComponent(feature_key)}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // keep it quiet or show toast only on errors — autosave can be noisy
        // success?.(`${title}: ${feature_key} gespeichert.`);
      } catch (e) {
        if (e?.response?.status === 401) {
          error?.(t("feature_setup.errors.unauthorized", { title }));
        } else {
          error?.(t("feature_setup.errors.save", { title, feature: feature_key }));
        }
      } finally {
        setSavingKeys((prev) => ({ ...prev, [feature_key]: false }));
      }
    },
    [apiUrl, token, buildSinglePayload, error, t, title]
  );

  const scheduleSave = useCallback(
    (feature_key) => {
      if (hydratingRef.current) return; // don’t autosave while applying backend state

      // debounce per feature_key
      const existing = timersRef.current[feature_key];
      if (existing) clearTimeout(existing);

      timersRef.current[feature_key] = setTimeout(() => {
        saveFeature(feature_key);
      }, 350);
    },
    [saveFeature]
  );

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => t && clearTimeout(t));
      timersRef.current = {};
    };
  }, []);

  // ---------------- UI handlers (now autosave) ----------------
  const setLeaf = useCallback(
    (category, page, subPage, value) => {
      const k = keyOf(category, page, subPage);
      setEnabled((prev) => {
        const next = { ...prev, [k]: value };
        // keep ref in sync immediately
        enabledRef.current = next;
        return next;
      });
      scheduleSave(page);
    },
    [scheduleSave]
  );

  const setPageEnabled = useCallback(
    (category, page, value) => {
      setEnabled((prev) => {
        const next = { ...prev, [keyOf(category, page, "")]: value };

        enabledRef.current = next;
        return next;
      });

      scheduleSave(page);
    },
    [scheduleSave]
  );

  // ---------------- filters / stats ----------------
  const filteredTree = useMemo(() => {
    const q = norm(query).toLowerCase();
    if (!q && !showOnlyEnabled) return tree;

    return tree
      .map((cat) => {
        const pages = cat.pages
          .map((p) => {
            const labelPage = p.page.toLowerCase();
            const pageMatches = q ? labelPage.includes(q) : true;

            const subPages = (p.subPages || []).filter((sp) => {
              const spMatches = q ? sp.toLowerCase().includes(q) : true;
              if (!spMatches) return false;
              if (!showOnlyEnabled) return true;
              return !!enabled[keyOf(cat.category, p.page, sp)];
            });

            const isLeaf = !p.subPages || p.subPages.length === 0;
            const leafEnabled = !!enabled[keyOf(cat.category, p.page, "")];

            if (isLeaf) {
              if (q && !pageMatches) return null;
              if (showOnlyEnabled && !leafEnabled) return null;
              return { ...p, subPages: [] };
            }

            if (q && !pageMatches && subPages.length === 0) return null;
            if (showOnlyEnabled && subPages.length === 0) return null;

            return { ...p, subPages };
          })
          .filter(Boolean);

        return { ...cat, pages };
      })
      .filter((cat) => cat.pages.length > 0);
  }, [tree, query, showOnlyEnabled, enabled]);

  const stats = useMemo(() => {
    const keys = Object.keys(enabled);
    const on = keys.filter((k) => enabled[k]).length;
    return { total: keys.length, on, off: keys.length - on };
  }, [enabled]);

  // ---------------- render ----------------
  if (loadingState) {
    return (
      <AppCard>
        <AppCardBody>
          <AppLoadingOverlay open scope="contained" />
        </AppCardBody>
      </AppCard>
    );
  }

  return (
    <AppFieldStack size="md">
      <AppToolbar
        left={(
          <AppField label={t("feature_setup.search_label")} size="md">
            <AppInput
              type="search"
              clearable
              value={query}
              placeholder={t("feature_setup.search", { title })}
              onChange={(event) => setQuery(event.target.value)}
            />
          </AppField>
        )}
        right={(
          <AppChip variant="neutral" appearance="outlined" size="md">
            {t("feature_setup.active_count", { active: stats.on, total: stats.total })}
          </AppChip>
        )}
      />

      {filteredTree.length === 0 ? (
        <AppCallout
          variant="info"
          title={t("feature_setup.no_results")}
          description={t("feature_setup.no_results_description")}
        />
      ) : null}

      {filteredTree.map((category) => (
        <AppFieldStack key={category.category} size="sm">
          {category.pages.map((page) => {
            const hasChildren = page.subPages.length > 0;
            const parentState = computeParentState(
              { category: category.category, page: page.page, subPages: page.subPages },
              enabled
            );
            const savingThis = Boolean(savingKeys[page.page]);

            return (
              <AppCard key={`${category.category}::${page.page}`}>
                <AppCardHeader
                  title={page.page}
                  titleVariant="compact"
                  action={(
                    <>
                      {hasChildren && parentState.partial ? (
                        <AppChip variant="neutral" appearance="outlined" size="sm">
                          {t("feature_setup.partial")}
                        </AppChip>
                      ) : null}
                      {savingThis ? (
                        <AppChip variant="info" appearance="semifilled" size="sm">
                          {t("feature_setup.saving")}
                        </AppChip>
                      ) : null}
                      <AppSwitch
                        checked={parentState.checked}
                        aria-label={t("feature_setup.toggle_feature", { feature: page.page })}
                        onChange={(event) =>
                          setPageEnabled(
                            category.category,
                            page.page,
                            event.target.checked
                          )
                        }
                      />
                    </>
                  )}
                />
                {hasChildren ? (
                  <AppCardBody>
                    <AppFieldStack size="sm">
                      {page.subPages.map((subPage) => {
                        const subPageKey = keyOf(category.category, page.page, subPage);
                        return (
                          <AppSwitch
                            key={subPageKey}
                            label={subPage}
                            disabled={!parentState.checked}
                            checked={Boolean(enabled[subPageKey])}
                            onChange={(event) =>
                              setLeaf(
                                category.category,
                                page.page,
                                subPage,
                                event.target.checked
                              )
                            }
                          />
                        );
                      })}
                    </AppFieldStack>
                  </AppCardBody>
                ) : null}
              </AppCard>
            );
          })}
        </AppFieldStack>
      ))}
    </AppFieldStack>
  );
}
